'use client'

import { useEffect, useRef, useState } from 'react'

/**
 * Becomes true once the element comes within `rootMargin` of the viewport, and stays true.
 *
 * For CSS background images, which `loading="lazy"` can't defer: an element with an inline
 * `background-image` makes the browser fetch it as soon as the element is rendered, even far
 * below the fold. Setting the background only once this is true defers the download the same way.
 */
export function useNearViewport<T extends Element>(rootMargin = '600px') {
  const ref = useRef<T>(null)
  const [near, setNear] = useState(false)

  useEffect(() => {
    const element = ref.current
    if (!element || near) return
    if (typeof IntersectionObserver === 'undefined') {
      setNear(true)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true)
          observer.disconnect()
        }
      },
      { rootMargin },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [near, rootMargin])

  return { ref, near }
}
